import {Component, createRef, Fragment, RefObject} from "react";
import {CableElements, NHCPinoutDevices} from "src/model/api/nhc-pinout-devices";
import { ts } from "src/utils/translate"
import { SVG_TEXT_HELPER } from "src/utils/svgTextHelper"
import * as d3 from "d3";

interface NHCBusPinoutProps {
  projectId: string | undefined;
  nhcPinoutData: NHCPinoutDevices;
  width: number;
  height: number;
}

interface NHCBusPinoutState {
  visibleRef: false;
  width : 842;
  height: 595;
  rasterSize: number;
  fontSizes: {
    /**
     * Size of port name texts on devices.
     */
    portName: number;
    /**
     * Size of text that's on/after a cable coming out from a port.
     */
    portAdditionalText: number;
    /**
     * Size of text that's on/after a cable coming out from a menorah group port.
     */
    menorahAdditionalText: number;
    /**
     * Size of text that's on/after a cable coming out from a port on an IT device.
     */
    itAdditionalText: number;
    /**
     * Size of a cable's name.
     */
    cableName: number;
    /**
     * Size of a tree bus line's name; that's before the line on a tree bus page (eg. "Line 1").
     */
    busLineText: number;
    /**
     * Size of a tree bus item's description text. Eg. "Living Room<br>Telephone".
     */
    busEntity: number;
    busEntitySymbol: number;
    /**
     * The text that tells the customers that this is not a 100% correct plan.
     */
    safetyText: number;
    itNameText: number;
    /**
     * The text under resistors (usually their resistance)
     */
    resistorText: number;
    portNLabelName: number;
    deviceName: number;
    cableTypeName: number;
  };
  margin: number;
  devExtraMargin: number;
  /**
   * The space reserved for the label text before tree bus lines (eg. "Line 1").
   */
  busLineTextWidth: number;
  /**
   * Number of lines below each other on tree bus pages.
   */
  busLinesPerPage_old: number;
  busLinesPerPage: number;
  /**
   * Width of an entity on a bus.
   */
  busEntitySize: number;
  /**
   * Width and height of a bus entity icon
   */
  busIconSize: number;
  /**
   * Distance of tree devices.
   */
  busDistance: number;
  busTextToLineDist: number;
  busLineToWireDist: number;
  /**
   * Distance of (non-tree) devices.
   */
  deviceDistance: number;
  /**
   * Stroke width of cables
   */
  cableStroke: number;
  /**
   * Vertical distance of cables.
   */
  pinoutDevCablesDist: number;
  /**
   * Vertical distance of the uppermost cable and the bottom of the devices.
   */
  cableDevSpace: number/* * 15 */;
  /**
   * The space between cables on a device's left side (where the upper ports connect to bottom cables).
   */
  leftCableSpace: number;
  /**
   * The default space between cables coming out of ports and the ports' labels.
   */
  defaultPortLabelDistance: number;
  /**
   * The length of the short "cable" coming out of a port when it's connected to a plain text.
   */
  portPlainTextDistance: number;
  /**
   * The vertical difference of grouped ports' lines.
   */
  menorahDistanceIncrementY: number;
  deviceNameMargin: {
    top: number;
    bottom: number;
  };
  portNameMargin: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  labelNameMargin: {
    left: number;
    right: number;
  };
  data: []
}

export class NHCBusPinout extends Component<NHCBusPinoutProps, NHCBusPinoutState> {

  private readonly svgContainer: RefObject<any>;

  constructor(props: NHCBusPinoutProps) {
    super(props);
    const rasterSize = 8;

    this.state = {
      visibleRef: false,
      width : 842,
      height: 595,
      rasterSize: rasterSize,
      fontSizes: {
        portName: rasterSize * 0.75,
        portAdditionalText: rasterSize * 0.9,
        menorahAdditionalText: rasterSize * 0.75,
        itAdditionalText: rasterSize * 1.2,
        cableName: rasterSize * 1,
        busLineText: rasterSize * 1.4,
        busEntity: rasterSize * 1.1,
        busEntitySymbol: rasterSize * 0.95,
        safetyText: rasterSize,
        itNameText: rasterSize * 1.5,
        resistorText: rasterSize * 0.9,
        portNLabelName: rasterSize * 1.5,
        deviceName: rasterSize * 2,
        cableTypeName: rasterSize * 1.25,
      },
      margin : rasterSize * 3,
      devExtraMargin : rasterSize,
      busLineTextWidth : 60,
      busLinesPerPage_old : 5,
      busLinesPerPage : 5,
      busEntitySize : rasterSize * 5,
      busIconSize : rasterSize * 4,
      busDistance : rasterSize * 1,
      busTextToLineDist : rasterSize * 2,
      busLineToWireDist : rasterSize * 1,
      deviceDistance : rasterSize * 1,
      cableStroke : 1,
      pinoutDevCablesDist : rasterSize * 2,
      cableDevSpace : rasterSize * 2 /* * 15 */,
      leftCableSpace : rasterSize,
      defaultPortLabelDistance : rasterSize * 1.5,
      portPlainTextDistance : rasterSize * 2,
      menorahDistanceIncrementY : rasterSize / 2,
      deviceNameMargin : {
        top: rasterSize,
        bottom: rasterSize
      },
      portNameMargin : {
        top: 0,
        bottom: rasterSize * 2,
        left: rasterSize,
        right: rasterSize * 2
      },
      labelNameMargin : {
        left: rasterSize * 2,
        right: rasterSize * 2
      },
      data: []
    };
    this.svgContainer = createRef<HTMLDivElement>();
  }

  componentDidMount() {
    if (this.props.nhcPinoutData) {
      this.drawElements();
    }
  }

  componentDidUpdate(prevProps: NHCBusPinoutProps) {
    if ((prevProps.nhcPinoutData !== this.props.nhcPinoutData)) {
      this.drawElements();
    }
  }

  render() {
    return <Fragment>
      <div ref={this.svgContainer}></div>
    </Fragment>;
  }

  private appendDefsToSVG(svg: any) {
    svg.append('defs')
    .append('marker')
    .attr('id', 'head')
    .attr('orient', 'auto')
    .attr('markerWidth', '4')
    .attr('markerHeight', '4')
    .attr('refX', '0.1')
    .attr('refY', '2')
    .append('path')
    .attr('d', 'M0,0 V4 L4,2 Z')
    .attr('fill', 'black')
    return svg;
  }

  private insertSafetyText(container: any) {
    const {width, margin, fontSizes} = this.state;
    container.insert("text")
      .text(ts('q_safety_text', 'This is not an actual electrical plan. Please contact your electrician for guidance.'))
      .attr("font-size", fontSizes.safetyText)
      .attr("x", width - margin * 2)
      .attr("y", margin / 2 + fontSizes.safetyText / 2)
      .attr("text-anchor", "end")
      .attr("fill", "lightgrey");
  }

  private drawText(
      cont: any,
      text: string,
      size: number,
      x: number,
      y: number,
      rot: number,
      anchor: string = 'start',
      maxWidth: number | null = null
  ) {
    const textElem = cont.append('text')
      .attr('font-size', size)
      .attr('text-anchor', anchor)
      .attr('x', x)
      .attr('y', y)
      .attr('transform', `rotate(${rot} ${x} ${y})`);
    if (maxWidth) {
      var isUnderlined = false;
      if (text.split(' ').length === 1 && text.indexOf('_') !== -1) {
        isUnderlined = true;
        text = text.split('_').join(' ');
      }
      var parts = SVG_TEXT_HELPER.wrapLabel(text, maxWidth, size);
      parts.forEach(function (part, idx) {
        if (isUnderlined) {
          part = part.split(' ').join('_');
        }
        textElem.append('tspan')
          .attr('x', x)
          .attr('dy', idx === 0 ? 0 : size)
          .text(part);
      });
    }
    else {
      textElem.text(text);
    }
    return textElem;
  }

  private renderNewLTreeLineIfTooMuchDevices(
      cont: any,
      linePlusMarginRight: { x: number, y: number },
      xPos: number,
      roomYPos: number
  ) {
    const {cableStroke} = this.state;

    cont.insert('line')
      .attr('stroke', 'black')
      .attr('stroke-width', cableStroke)
      .attr('x1', xPos)
      .attr('y1', roomYPos)
      .attr('x2', xPos + 23)
      .attr('y2', roomYPos);

    xPos += 23;

    cont.insert('line')
      .attr('stroke', 'black')
      .attr('stroke-width', cableStroke)
      .attr('x1', xPos)
      .attr('y1', roomYPos)
      .attr('x2', xPos)
      .attr('y2', roomYPos + 55);

    roomYPos += 55;

    cont.insert('line')
      .attr('stroke', 'black')
      .attr('stroke-width', cableStroke)
      .attr('x1', linePlusMarginRight.x + 15)
      .attr('y1', roomYPos)
      .attr('x2', xPos)
      .attr('y2', roomYPos);

    xPos = linePlusMarginRight.x + 15;

    cont.insert('line')
      .attr('stroke', 'black')
      .attr('stroke-width', cableStroke)
      .attr('x1', xPos)
      .attr('y1', roomYPos)
      .attr('x2', xPos)
      .attr('y2', roomYPos + 55);
  }

  private safeGetBBox(svgElement: any) {
    let tempDiv = document.createElement('div');
    tempDiv.setAttribute('style', "position:absolute; visibility:hidden; width:0; height:0");
    document.body.appendChild(tempDiv);
    let tempSvg = document.createElementNS("http://www.w3.org/2000/svg", 'svg');
    tempDiv.appendChild(tempSvg);
    let tempEl = svgElement.cloneNode(true);
    tempSvg.appendChild(tempEl);
    let bb = tempEl.getBBox();
    document.body.removeChild(tempDiv);
    return bb;
  }

  private getOutputPolylinePointsAround(
      rect: {
        x: number,
        y: number,
        width: number,
        height: number
      }
  ): [number, number][] {
    return [
      [rect.x, rect.y], // top left
      [rect.x + rect.width, rect.y], // top right
      [rect.x + rect.width + rect.height / 2, rect.y + rect.height / 2], // right center
      [rect.x + rect.width, rect.y + rect.height], // bottom right
      [rect.x, rect.y + rect.height],  // bottom left
      [rect.x, rect.y], // top left again
    ];
  }

  private renderCables(data: CableElements) {
    const {margin, width, height, fontSizes, labelNameMargin } = this.state;

    let cont: any = null;

    if (Object.keys(data).length > 0) {
      cont = this.createSvgContainer();
    }

    const originalYPos = 48;
    const originalXPos = margin;

    let XPos = originalXPos;
    let YPos = originalYPos;

    for (const [dinDeviceName, dins] of Object.entries(data)) {

      for (const [dinId, elements] of Object.entries(dins)) {

        cont.append('text')
          .attr('x', XPos)
          .attr('y', YPos)
          .attr('font-size', fontSizes.deviceName)
          .attr('fill', 'black')
          .attr('text-anchor', 'start')
          .text(dinDeviceName);

        YPos += 30;

        for (const [elementId, element] of Object.entries(elements)) {

          /* PORT */
          cont.append('text')
            .attr('x', XPos + margin)
            .attr('y', YPos)
            .attr('font-size', fontSizes.portNLabelName)
            .attr('fill', 'black')
            .attr('text-anchor', 'start')
            .text(element['connection_type']);

          /* ARROW START */

          cont.insert('line')
            .attr('stroke', 'black')
            .attr('stroke-width', 1.5)
            .attr('x1', XPos + 65)
            .attr('y1', YPos - 4)
            .attr('x2', XPos + 61)
            .attr('y2', YPos - 8);

          cont.insert('line')
            .attr('stroke', 'black')
            .attr('stroke-width', 1.5)
            .attr('x1', XPos + 65)
            .attr('y1', YPos - 4)
            .attr('x2', XPos + 61)
            .attr('y2', YPos);

          /* ARROW END */

          const labelName = cont.append('text')
            .attr('x', width - margin - labelNameMargin.right)
            .attr('y', YPos)
            .attr('font-size', fontSizes.portNLabelName)
            .attr('fill', 'black')
            .attr('text-anchor', 'end')
            .text(element['element_name']).node();

          const labelNameBBox = this.safeGetBBox(labelName);

          const bboxWithMargin = {
            x: labelNameBBox.x - 2,
            y: labelNameBBox.y,
            width: labelNameBBox.width,
            height: labelNameBBox.height,
          };


          cont.insert('line')
            .attr('stroke', 'black')
            .attr('stroke-width', 0.5)
            .attr('x1', XPos + 60)
            .attr('y1', YPos - 4)
            .attr('x2', labelNameBBox.x - 3)
            .attr('y2', YPos - 4);

          // LABEL OUTPUT POLYLINE
          const endPolyLinePoints = this.getOutputPolylinePointsAround(bboxWithMargin);

          const lineFunction = d3.line()
            .x(function (d) {
              return d[0];
            })
            .y(function (d) {
              return d[1];
            })
            .curve(d3.curveLinear);

          cont.append("path")
          .attr("d", lineFunction(endPolyLinePoints))
          .attr("stroke", 'black')
          .attr("stroke-width", 0.5)
          .attr("fill", "none");

          cont.append('text')
            .attr('x', (XPos + width - margin) / 2)
            .attr('y', YPos - 10)
            .attr('font-size', fontSizes.cableTypeName)
            .attr('fill', 'black')
            .attr('text-anchor', 'middle')
            .text(element['cable_type']);

          YPos += 28;
        }

        if (YPos + 14 > height - 48) {
          cont = this.createSvgContainer();
          YPos = originalYPos;
          XPos = originalXPos;
        } else {
          YPos += 14;
          XPos = originalXPos;
        }

      }

      if (YPos > height) {
        cont = this.createSvgContainer();
        YPos = originalYPos;
        XPos = originalXPos;
      }
    }

  }

  private renderDaliBuses(busData: any) {
    let cont: any = null;

    const imgSize = 26;

    const originalYPos = 60;
    const originalXPos = 10;

    let roomYPos = originalYPos;
    let roomXPos = originalXPos;

    const {margin, busEntitySize, fontSizes, busLineTextWidth, cableStroke } = this.state;

    const linePlusMarginRight = { x: margin + busLineTextWidth - 5, y: roomYPos + busEntitySize / 2 + fontSizes.busLineText / 2 * 0.7 };

    /* számolni egy svg-n a lehelyezhető sorokat , ami max 5 lehet */
    let rows = 0;

    let lineId = 0;

    for (const [busLineId, devices] of Object.entries(busData) as [any, any]) {

      if (rows == 0) {

        cont = this.createSvgContainer();

        cont.append("text")
        .text('DALI Bus')
        .attr("x", linePlusMarginRight.x - 45)
        .attr("y", roomYPos - 4)
        .attr("transform", `rotate(-90 ${linePlusMarginRight.x - 45} ${roomYPos - 4})`)
        .attr('text-anchor', 'end');

      }

      cont.append("text")
      .text('Line ' +  (++lineId) )
      .attr("x", linePlusMarginRight.x - 35)
      .attr("y", roomYPos + 4)
      .attr("font-size", fontSizes.busLineText);

      rows++;

      let countedDevices = 0;

      let xPos = linePlusMarginRight.x + 15 + (cableStroke * 1.5 / 2);

      let allDevices = Object.keys(devices).length;

      for(const [ deviceId, device ] of Object.entries(devices) as [any, any]) {

        countedDevices++;

        cont.append('circle')
          .attr('cx', xPos)
          .attr('cy', roomYPos)
          .attr('r', cableStroke * 1.5)
          .attr('fill', 'black')
          .attr('stroke', 'black')
          .attr('stroke-width', cableStroke);

        cont.insert('line')
          .attr('stroke', 'black')
          .attr('stroke-width', cableStroke)
          .attr('x1', xPos)
          .attr('y1', roomYPos)
          .attr('x2', xPos)
          .attr('y2', roomYPos + 30);

        cont.append('svg:image')
          .attr('x', xPos - (imgSize / 2))
          .attr('y', roomYPos + 30)
          .attr('xlink:href', device.icon)
          .attr('width', imgSize)
          .attr('height', imgSize);

        this.drawText(cont, device.productName, fontSizes.busEntitySymbol, xPos, roomYPos + 40 + (imgSize), 0, 'middle', busEntitySize);

        if (countedDevices != allDevices) {
          cont.insert('line')
            .attr('stroke', 'black')
            .attr('stroke-width', cableStroke)
            .attr('x1', xPos)
            .attr('y1', roomYPos)
            .attr('x2', xPos + 48)
            .attr('y2', roomYPos);

          xPos += 48;
        }

        if (countedDevices > 14) {

          countedDevices = 0;

          xPos = linePlusMarginRight.x + 15 + (cableStroke * 1.5 / 2);

          if (rows > 4) {

            roomYPos = originalYPos;

            cont = this.createSvgContainer();

            cont.append("text")
            .text('DALI Bus')
            .attr("x", linePlusMarginRight.x - 45)
            .attr("y", roomYPos - 4)
            .attr("transform", `rotate(-90 ${linePlusMarginRight.x - 45} ${roomYPos - 4})`)
            .attr("font-size", 'Dali Line')
            .attr('text-anchor', 'end');

            cont.append("text")
            .text('Line ' +  (lineId) )
            .attr("x", linePlusMarginRight.x - 35)
            .attr("y", roomYPos + 4)
            .attr("font-size", fontSizes.busLineText);

            rows = 1;

          }
          else {
            rows++;
            roomYPos += 110;
          }

          cont.insert('line')
            .attr('stroke', 'black')
            .attr('stroke-width', cableStroke)
            .attr('x1', xPos - 15)
            .attr('y1', roomYPos)
            .attr('x2', xPos)
            .attr('y2', roomYPos);
        }
      }

      roomYPos += 110;

      if (rows > 4) {
        rows = 0;
        roomXPos = originalXPos;
        roomYPos = originalYPos;
      }

    }

  }

  private renderTreeBuses(busData: any, busType: string) {

    let cont: any = null;

    const imgSize = 26;

    const originalYPos = 60;
    const originalXPos = 10;

    let roomYPos = originalYPos;
    let roomXPos = originalXPos;

    const {margin, busEntitySize, fontSizes, busLineTextWidth, cableStroke } = this.state;

    let countedRooms = 0;
    const allRooms = Object.keys(busData).length;

    const linePlusMarginRight = { x: margin + busLineTextWidth - 5, y: roomYPos + busEntitySize / 2 + fontSizes.busLineText / 2 * 0.7 };

    /* számolni egy svg-n a lehelyezhető sorokat , ami max 5 lehet */
    let rows = 0;

    let localPage = 1;

    for (const [roomId, devices] of Object.entries(busData) as [any, any]) {

      if (rows == 0) {
        cont = this.createSvgContainer();


        cont.append("text")
        .text('Line 1')
        .attr("x", linePlusMarginRight.x - 35)
        .attr("y", roomYPos + 4)
        .attr("font-size", fontSizes.busLineText);

        cont.append("text")
        .text(busType)
        .attr("x", linePlusMarginRight.x - 45)
        .attr("y", roomYPos - 4)
        .attr("transform", `rotate(-90 ${linePlusMarginRight.x - 45} ${roomYPos - 4})`)
        .attr("font-size", fontSizes.busLineText)
        .attr('text-anchor', 'end');

      }
      rows++;

      countedRooms++;

      let countedDevices = 0;

      cont.append('circle')
        .attr('cx', linePlusMarginRight.x + 5)
        .attr('cy', roomYPos)
        .attr('r', cableStroke * 1.5)
        .attr('fill', 'white')
        .attr('stroke', 'black')
        .attr('stroke-width', cableStroke);

      let circlePositionY = roomYPos;

      let xPos = linePlusMarginRight.x + 5 + (cableStroke * 1.5 / 2);

      cont.insert('line')
        .attr('stroke', 'black')
        .attr('stroke-width', cableStroke)
        .attr('x1', xPos)
        .attr('y1', roomYPos)
        .attr('x2', xPos + 10)
        .attr('y2', roomYPos);

      xPos = xPos + 10;

      for(const [ deviceArrId, device ] of Object.entries(devices) as [any, any]) {

        countedDevices++;

        cont.insert('line')
          .attr('stroke', 'black')
          .attr('stroke-width', cableStroke)
          .attr('x1', xPos)
          .attr('y1', roomYPos)
          .attr('x2', xPos + 23)
          .attr('y2', roomYPos);

        xPos += 23;

        cont.append('svg:image')
          .attr('x', xPos)
          .attr('y', roomYPos - imgSize / 2)
          .attr('xlink:href', device.icon)
          .attr('width', imgSize)
          .attr('height', imgSize);

        xPos += imgSize;

        this.drawText(cont, device.productName, fontSizes.busEntitySymbol, xPos - (imgSize / 2), roomYPos + imgSize / 2 + fontSizes.busLineText + 2, 0, 'middle', busEntitySize);

        if (countedDevices >= 14) {
          /* új sor */
          rows++;
          this.renderNewLTreeLineIfTooMuchDevices(cont, linePlusMarginRight, xPos, roomYPos);
          roomYPos += 110;
          xPos = linePlusMarginRight.x + 15;
          countedDevices = 1;
        }

      }

      roomYPos += 110;

      if (countedRooms < allRooms) {
        cont.append('line')
          .attr('x1', linePlusMarginRight.x + 5)
          .attr('y1', circlePositionY + (cableStroke * 1.5 / 2))
          .attr('x2', linePlusMarginRight.x + 5)
          .attr('y2', (rows > 4 ) ? ( roomYPos - 55 ) : roomYPos)
          .attr('stroke', 'black')
          .attr('stroke-width', cableStroke);
      }

      localPage++;

      if (rows > 4) {
        rows = 0;
        roomXPos = originalXPos;
        roomYPos = originalYPos;
      }

    }
  }

  private renderAirElements(airElements: any)
  {
    let cont = null;

    const imgSize = 26;

    const originalYPos = 60;
    const originalXPos = 10;

    let YPos = originalYPos;
    let XPos = originalXPos;

    const {margin, busEntitySize, fontSizes, busLineTextWidth } = this.state;

    const linePlusMarginRight = { x: margin + busLineTextWidth - 5, y: YPos + busEntitySize / 2 + fontSizes.busLineText / 2 * 0.7 };

    if (!Object.keys(airElements).length) {
      return;
    }

    cont = this.createSvgContainer();

    cont.append("text")
    .text('Wireless Line')
    .attr("x", linePlusMarginRight.x - 45)
    .attr("y", YPos)
    .attr("font-size", fontSizes.deviceName)
    .attr('text-anchor', 'start');

    YPos += 15;

    for (const [index, element] of Object.entries(airElements) as [any, any]) {

      let elementCounter = 1;

      cont.append('svg:image')
        .attr('x', linePlusMarginRight.x + XPos - (imgSize / 2))
        .attr('y', YPos)
        .attr('xlink:href', element.icon)
        .attr('width', imgSize)
        .attr('height', imgSize);

      cont.append("text")
      .text(element.name)
      .attr("x", linePlusMarginRight.x + XPos)
      .attr("y", YPos + imgSize + 10)
      .attr("font-size", fontSizes.cableName)
      .attr('text-anchor', 'middle');

      XPos += 65;

      elementCounter += 1;

      if (XPos > this.props.width - (linePlusMarginRight.x + originalXPos) ) {
        elementCounter = 1;
        YPos = YPos + imgSize + 35;
        XPos = originalXPos;
      }

      if (YPos > this.props.height) {
        cont = this.createSvgContainer();
        YPos = originalYPos;
        XPos = originalXPos;
      }

    }
  }

  private createSvgContainer()
  {
    const { width, height } = this.state;
    d3.select(this.svgContainer.current).html("");
    d3.select(this.svgContainer.current).text("");
    let cont = d3.select(this.svgContainer.current).insert("svg")
    .attr("width", '100%')
    .attr("height", '100%')
    .attr("viewBox", `0 0 ${ width } ${ height }`)
    .attr("class", "pinout lightshadow")
    .append("g");

    this.appendDefsToSVG(cont);
    this.insertSafetyText(cont);
    return cont;
  }

  private drawElements()
  {
    if (this.svgContainer.current) {

        if (this.props.nhcPinoutData != null && this.props.nhcPinoutData['treeElements']) {
          const treeData = this.props.nhcPinoutData['treeElements'];
          if (treeData.hasOwnProperty('TREE_BUS_FROM_CONTROLLER')) {
            this.renderTreeBuses(treeData['TREE_BUS_FROM_CONTROLLER'], 'Connected Controller');
          }

          if (treeData.hasOwnProperty('TREE_BUS_FROM_POWER_SUPPLY')) {
            this.renderTreeBuses(treeData['TREE_BUS_FROM_POWER_SUPPLY'], 'Power Supply');
          }

          if (treeData.hasOwnProperty('DALI')) {
            this.renderDaliBuses(treeData['DALI']);
          }

        if (this.props.nhcPinoutData != null && this.props.nhcPinoutData['airElements']) {
          const airData = this.props.nhcPinoutData['airElements'];
          this.renderAirElements(airData);
        }

        if (this.props.nhcPinoutData != null && this.props.nhcPinoutData['cableElements']) {
          const cableData = this.props.nhcPinoutData['cableElements'];
          if (cableData !== null) {
            this.renderCables(cableData);
          }
        }
      }
    }
  }
}
